import React, { useState, useRef } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../../../../util/reactIntl';
import { languagesHelper as language } from '../../../../../util/languages';

import {
  OutsideClickHandler,
  IconArrowHead,
  IconCategoryMultiday,
  IconCategoryDaytime,
  IconCategoryStays
} from '../../../../../components';

import css from './FilterCategories.module.css';

const CategoryDropdown = ({
  input,
  className,
  rootClassName,
  categories,
  alignLeft,
  onCountListingsByFilters,
  queryParams,
  setQueryParams,
  dropdownOpenUpwardClass,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [hasSelected, setHasSelected] = useState(false);
  const intl = useIntl();

  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
    if (!isOpen) {
      // reset the index used to navigate menu items using keyboard when the menu is closed
      setActiveIndex(-1);
    }
  };

  const handleOptionClick = option => {
    let value = '';

    if (option !== 'all-categories') {
      value = option;

      const newQueryParams = { ...queryParams, pub_categoryLevel1: option };
      setQueryParams(newQueryParams);

      // Keep category facet counts intact so all categories stay visible
      const { pub_categoryLevel1, ...paramsWithoutCategory } = newQueryParams;
      onCountListingsByFilters({ queryParams: paramsWithoutCategory });
    } else {
      const cloneQueryParams = structuredClone(queryParams);
      delete cloneQueryParams.pub_categoryLevel1;
      setQueryParams(cloneQueryParams);

      onCountListingsByFilters({ queryParams: cloneQueryParams });
    }

    input.onChange(value);
    setHasSelected(true);
  };

  // keydown handling when navigating menu using keyboard
  const handleKeyDown = e => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => {
        if (prev < allOptions.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => {
        if (prev > 0) {
          return prev - 1;
        }
        return prev;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = allOptions[activeIndex];
      if (selected) handleOptionClick(selected.id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  const hasCategoryValue = !!input.value;
  const hasSelection = hasCategoryValue || (hasSelected && input.value === '');

  const dropdownItemText = hasCategoryValue ? (
    language.labelsTranslator(input.value, intl)
  ) : hasSelected && input.value === '' ? (
    <FormattedMessage id="PageBuilder.SearchCTA.CategoryFilter.selectAll" />
  ) : (
    <FormattedMessage id="PageBuilder.SearchCTA.CategoryFilter.placeholder" />
  );

  const rootClass = rootClassName || css.root;
  const classes = classNames(rootClass, className);

  // append the "all categories" option to the list of categories
  const allOptions = [
    {
      id: 'all-categories',
      name: <FormattedMessage id="PageBuilder.SearchCTA.CategoryFilter.selectAll" />,
      count: null,
    },
    ...categories,
  ];

  const optionRefs = useRef([]);

  const categoryIcons = {
    "category-tours": <IconCategoryMultiday />,
    "category-experiences": <IconCategoryDaytime />,
    "category-stay": <IconCategoryStays />,
  };

  return (
    <OutsideClickHandler className={classes} onOutsideClick={() => setIsOpen(false)}>
      <div className={css.dropdownContainer}>
        <div className={css.dropdownItemPlaceholder}>
          <FormattedMessage
            id="PageBuilder.SearchCTA.CategoryFilter.placeholderDescription"
            values={{ lineBreak: <br /> }}
          />
        </div>
        <div
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-owns="category-listbox"
          aria-controls="category-listbox"
          tabIndex={0}
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          className={classNames(css.toggleButton, {
            [css.unselected]: !hasSelection && !isOpen,
            [css.placeholderOpened]: isOpen && !hasSelection,
          })}
        >
          <span className={classNames(css.dropdownItem, hasSelection && css.dropdownItemSelected)}>
            {dropdownItemText}
          </span>
          <span className={classNames(css.chevron, isOpen && css.isOpen)} >
            <IconArrowHead direction={isOpen ? "up" : "down"} />
          </span>
        </div>

        {isOpen && (
          <ul
            className={classNames(css.dropdownContent, dropdownOpenUpwardClass, {
              [css.alignLeft]: alignLeft,
            })}
            role="listbox"
            id="category-listbox"
          >
            {allOptions.map(({ id, name, count }, index) => {
              const isSelected =
                id === input.value || (id === 'all-categories' && hasSelected && input.value === '');
              const isActive = index === activeIndex;
              return (
                <li
                  key={id}
                  ref={option => (optionRefs.current[index] = option)}
                  className={classNames(css.option, {
                    [css.activeOption]: isActive,
                    [css.selectedOption]: isSelected,
                  })}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleOptionClick(id)}
                >
                  <span
                    className={classNames(css.selectMark, {
                      [css.selectMarkOn]: isSelected,
                    })}
                    aria-hidden
                  />
                  <span
                    className={isSelected ? css.dropdownItemBorderSelected : css.dropdownItemBorder}
                  />
                  {categoryIcons[id]}
                  {id === 'all-categories' ? name : language.labelsTranslator(id, intl)}
                  <span className={css.count}>
                    {id !== 'all-categories'
                      ? count
                      : allOptions.reduce((acc, curr) => acc + Number(curr.count), 0)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </OutsideClickHandler>
  );
};

// Final Form wrapper
const FilterCategories = props => {
  const { className, rootClassName, ...rest } = props;
  return (
    <Field
      {...rest}
      name="pub_categoryLevel1"
      component={CategoryDropdown}
      className={className}
      rootClassName={rootClassName}
    />
  );
};

export default FilterCategories;
